import { checkEmailExists } from '@/modules/Marketing/services'
import CheckIcons from '@/icons/CheckIcons'
import React, { useEffect, useState } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormItem, Loading } from 'vnetwork-ui'

interface EmailFieldUpdateProps {
    control: any
    initialEmail?: string
    disabled?: boolean
    onValidationChange?: (isValid: boolean) => void
}

const EmailFieldUpdate: React.FC<EmailFieldUpdateProps> = ({
    control,
    initialEmail = '',
    disabled = false,
    onValidationChange,
}) => {
    const { t } = useTranslation()
    const [isChecking, setIsChecking] = useState(false)
    const [isValid, setIsValid] = useState(false)
    const [validationMessage, setValidationMessage] = useState('')

    const emailValue = useWatch({
        control,
        name: 'contact_source_email',
    })

    const { errors } = useFormState({ control })
    const emailError = errors?.contact_source_email

    useEffect(() => {
        const checkEmail = async () => {
            // Reset states
            setIsValid(false)
            setValidationMessage('')

            // Basic validation
            if (!emailValue) return

            // Nếu email giống với email ban đầu, bỏ qua validation
            if (emailValue === initialEmail) {
                setIsValid(true)
                onValidationChange?.(true)
                return
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(emailValue)) {
                return
            }

            // Check email existence
            setIsChecking(true)
            try {
                const result = await checkEmailExists(emailValue)

                if (!result) {
                    // API error, but don't block
                    setIsValid(true)
                    onValidationChange?.(true)
                    return
                }

                if (!result.exists) {
                    // Email available
                    setIsValid(true)
                    onValidationChange?.(true)
                } else {
                    // Email exists
                    setIsValid(false)
                    onValidationChange?.(false)

                    if (result.exists_in_customers) {
                        setValidationMessage(t('email-exists-in-customers'))
                    } else if (result.exists_in_customer_contacts) {
                        setValidationMessage(
                            t('email-exists-in-customer-contacts')
                        )
                    } else if (result.exists_in_contact_sources) {
                        setValidationMessage(
                            t('email-exists-in-contact-sources')
                        )
                    } else {
                        setValidationMessage(t('email-already-exists'))
                    }
                }
            } catch (error) {
                console.error(error)
                // On error, allow to continue
                setIsValid(true)
                onValidationChange?.(true)
            } finally {
                setIsChecking(false)
            }
        }

        const timeoutId = setTimeout(() => {
            checkEmail()
        }, 500) // Debounce 500ms

        return () => clearTimeout(timeoutId)
    }, [emailValue, initialEmail, t, onValidationChange])

    return (
        <div className="relative">
            <FormItem
                control={control}
                name="contact_source_email"
                type="textfield"
                label={t('email')}
                placeholder={t('email')}
                disabled={disabled}
            />
            {!disabled && (
                <div className="absolute right-3 top-[4px] flex items-center">
                    {isChecking && <Loading.Ring width={16} height={16} />}
                    {!isChecking &&
                        isValid &&
                        emailValue &&
                        !emailError &&
                        emailValue !== initialEmail && (
                            <CheckIcons className="w-4 h-4 text-success" />
                        )}
                </div>
            )}

            {/* Custom validation message */}
            {!disabled && !isChecking && validationMessage && !emailError && (
                <p className="text-error text-xs mt-1">{validationMessage}</p>
            )}
        </div>
    )
}

export default EmailFieldUpdate
